import { env } from 'next-runtime-env/build/script/env';

// Google Analytics configuration and utilities
export const GA_MEASUREMENT_ID = env('NEXT_PUBLIC_GA_MEASUREMENT_ID');

// Check if Google Analytics is enabled
export const isGAEnabled = () => {
  return typeof window !== 'undefined' && GA_MEASUREMENT_ID;
};

// Custom event tracking
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!isGAEnabled()) return;

  // Use gtag for GA4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Page view tracking
export const trackPageView = (url: string, title?: string) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID!, {
      page_path: url,
      page_title: title,
    });
  }
};

// User property tracking
export const setUserProperty = (property: string, value: string) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      [property]: value,
    });
  }
};

// Enhanced ecommerce tracking
export const trackPurchase = (
  transactionId: string,
  value: number,
  currency: string = 'USD',
  items?: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>
) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }
};

// Form submission tracking
export const trackFormSubmission = (formName: string, formId?: string) => {
  trackEvent('form_submit', 'engagement', formName);
};

// Button click tracking
export const trackButtonClick = (
  buttonName: string,
  buttonLocation?: string
) => {
  trackEvent('button_click', 'engagement', buttonName, undefined);
};

// Error tracking
export const trackError = (errorMessage: string, errorCode?: string) => {
  trackEvent('error', 'error', errorMessage);
};

// Performance tracking
export const trackPerformance = (metric: string, value: number) => {
  trackEvent('performance', 'web_vitals', metric, value);
};

// Custom dimensions and metrics
export const setCustomDimension = (dimensionIndex: number, value: string) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'custom_map', {
      [`dimension${dimensionIndex}`]: 'custom_dimension',
    });
    window.gtag('event', 'custom_dimension', {
      custom_dimension: value,
    });
  }
};

// Consent management
export const updateConsent = (consent: boolean) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent ? 'granted' : 'denied',
    });
  }
};

// Debug mode
export const enableDebugMode = () => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID!, {
      debug_mode: true,
    });
  }
};

// Disable debug mode
export const disableDebugMode = () => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID!, {
      debug_mode: false,
    });
  }
};

// Registration funnel tracking
export const trackFunnelStep = (
  stepName: string,
  stepNumber: number,
  hash?: string,
  additionalData?: Record<string, any>
) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'registration_funnel_step', {
      funnel_name: 'participant_registration',
      step_name: stepName,
      step_number: stepNumber,
      event_hash: hash,
      ...additionalData,
    });
  }
};

export const trackFunnelStart = (hash: string, eventName?: string) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_registration', {
      funnel_name: 'participant_registration',
      event_hash: hash,
      event_name: eventName,
    });
  }
};

export const trackFunnelComplete = (
  hash: string,
  eventsSelected?: string[]
) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'registration_complete', {
      funnel_name: 'participant_registration',
      event_hash: hash,
      events_count: eventsSelected?.length || 0,
      events_list: eventsSelected?.join(','),
    });
  }
};

export const trackFunnelAbandonment = (
  stepName: string,
  stepNumber: number,
  reason?: string
) => {
  if (!isGAEnabled()) return;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'registration_abandoned', {
      funnel_name: 'participant_registration',
      step_name: stepName,
      step_number: stepNumber,
      abandonment_reason: reason,
    });
  }
};

export const setFunnelSession = (hash: string, eventId?: string) => {
  if (!isGAEnabled()) return;

  setUserProperty('registration_hash', hash);
  if (eventId) {
    setUserProperty('target_event_id', eventId);
  }
};
